/**
 * FusionMsiMarketingLayout.jsx — shared marketing shell (backdrop, nav, footer).
 * Shaped by: md3c step 4b.
 * Invariants: disclosure, location note, and legal lines come from the brand
 * config. Empty config fields render nothing, so fusionmsi.com keeps today's
 * two captions and no copyright. Gradients stay in fusionMsiMarketingTokens.
 * Related: resolveMarketingBrandConfig.js, FusionMsiMarketingNav.jsx,
 * useMarketingBrandDocumentHead.js.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Container, Link, Stack, Typography } from '@mui/material';
import { isMd3cMarketingHost } from 'features/md3c/marketing/md3cMarketingRouting';
import CareStartTrademarkText from './CareStartTrademarkText';
import FusionMsiMarketingNav from './FusionMsiMarketingNav';
import GeoLocationSuggestBanner from './GeoLocationSuggestBanner';
import { FusionMsiIntroProvider } from './FusionMsiIntroContext';
import {
  FUSION_MSI_MARKETING_GRADIENT_DARK,
  FUSION_MSI_MARKETING_GRADIENT_LIGHT,
} from '../marketing/fusionMsiMarketingTokens';
import { resolveMarketingBrandConfig } from '../marketing/resolveMarketingBrandConfig';
import { useMarketingBrandDocumentHead } from '../marketing/useMarketingBrandDocumentHead';
import {
  isCareStartSplashPath,
  withCareStartSplashCopy,
} from '../marketing/careStartSplashCopy';

function FusionMsiMarketingLayout({ children, maxWidth = 'sm' }) {
  const { pathname } = useLocation();
  const brand = resolveMarketingBrandConfig();
  useMarketingBrandDocumentHead(brand);
  const footer = brand.footer || {};
  const captionSource = footer.caption || '';
  const footerDisclosure =
    captionSource && isCareStartSplashPath(pathname) && !isMd3cMarketingHost()
      ? withCareStartSplashCopy(captionSource)
      : captionSource;
  const locationNote = footer.locationNote || '';
  const legalLinks = Array.isArray(footer.links) ? footer.links : [];
  const hasLegalFooter = Boolean(
    footer.entityLine || footer.address || footer.copyright || legalLinks.length,
  );

  return (
    <FusionMsiIntroProvider>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <FusionMsiMarketingNav />
        <GeoLocationSuggestBanner />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: { xs: 4, md: 6 },
            px: 2,
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? FUSION_MSI_MARKETING_GRADIENT_DARK
                : FUSION_MSI_MARKETING_GRADIENT_LIGHT,
          }}
        >
          <Container maxWidth={maxWidth} sx={{ width: '100%' }}>
            {children}
          </Container>
          {footerDisclosure ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 6, maxWidth: 480, textAlign: 'center', display: 'block', px: 2 }}
            >
              <CareStartTrademarkText text={footerDisclosure} />
            </Typography>
          ) : null}
          {locationNote ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, maxWidth: 480, textAlign: 'center', display: 'block', px: 2 }}
            >
              {locationNote}
            </Typography>
          ) : null}
          {hasLegalFooter ? (
            <Box component="footer" sx={{ mt: 2, maxWidth: 480, textAlign: 'center', px: 2 }}>
              {footer.entityLine ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {footer.entityLine}
                </Typography>
              ) : null}
              {footer.address ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {footer.address}
                </Typography>
              ) : null}
              {footer.copyright ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {footer.copyright}
                </Typography>
              ) : null}
              {legalLinks.length ? (
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="center"
                  flexWrap="wrap"
                  sx={{ mt: 1 }}
                >
                  {legalLinks.map((link) =>
                    link.external ? (
                      <Link
                        key={link.href}
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="caption"
                        color="text.secondary"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <Link
                        key={link.href}
                        component={RouterLink}
                        to={link.href}
                        variant="caption"
                        color="text.secondary"
                      >
                        {link.label}
                      </Link>
                    ),
                  )}
                </Stack>
              ) : null}
            </Box>
          ) : null}
        </Box>
      </Box>
    </FusionMsiIntroProvider>
  );
}

FusionMsiMarketingLayout.propTypes = {
  children: PropTypes.node.isRequired,
  maxWidth: PropTypes.oneOf(['sm', 'md']),
};

export default FusionMsiMarketingLayout;
